import React, { useEffect, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import LinearProgress from 'components_new/atoms/LinearProgress'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import SectionHeader from 'components_new/molecules/SectionHeader'

import { RunningJob } from 'redux/selectors/internal/DataRuns'
import {
  RUNNING_PHASE_LABEL,
  RUNNING_PULSE_SX,
  describeRunningProgress,
  formatClock,
  formatDurationLong,
  jobLookupKey,
  latestExpectedFinish
} from '../utils'

/**
 * The elapsed time and the countdown are rendered from the current clock,
 * so they have to be re-rendered without waiting for the next poll. One
 * second is what makes it read as live rather than as a stale number.
 */
const TICK_INTERVAL_MS = 1000

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), intervalMs)

    return () => clearInterval(interval)
  }, [intervalMs])

  return now
}

interface RunningRowProps {
  job: RunningJob
  nowMs: number
  onSelectJobByName: (jobName: string, customerId?: string) => void
}

const RunningRow = (props: RunningRowProps) => {
  const { job, nowMs, onSelectJobByName } = props

  const { elapsedMin, remainingMin, progressPercent, isOverdue, overdueMin } =
    describeRunningProgress(job, nowMs)

  const etaText =
    progressPercent === null
      ? `Ingen historik att räkna på (${job.history_sample_size} lyckade körningar)`
      : isOverdue
        ? `Förväntades klar ${formatClock(
          job.expected_finish_at
        )} · ${formatDurationLong(overdueMin)} över`
        : `Klar ca ${formatClock(job.expected_finish_at)} · ${formatDurationLong(
          remainingMin ?? 0
        )} kvar`

  return (
    <Box
      onClick={() => onSelectJobByName(job.job_name, job.customer?.id)}
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: isOverdue ? 'error.light' : 'divider',
        borderRadius: '12px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: '12px 16px',
        '&:hover': { bgcolor: 'action.hover' }
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
        <Box sx={RUNNING_PULSE_SX}>
          <Tag color="info" label={RUNNING_PHASE_LABEL[job.phase]} />
        </Box>
        <Text sx={{ fontWeight: 600, minWidth: '120px' }} variant="body2">
          {job.customer?.name ?? 'Okänd kund'}
        </Text>
        <Text sx={{ flex: 1, minWidth: 0 }} variant="body2">
          {job.job_name}
          <Text component="span" sx={{ color: 'text.secondary', ml: 1 }}>
            {job.source_label}
          </Text>
        </Text>
        <Text
          sx={{
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 600,
            whiteSpace: 'nowrap'
          }}
          variant="body2"
        >
          {formatDurationLong(elapsedMin)}
        </Text>
      </Box>

      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            color={isOverdue ? 'error' : 'info'}
            value={progressPercent ?? undefined}
            variant={progressPercent === null ? 'indeterminate' : 'determinate'}
          />
        </Box>
        <Text
          sx={{
            color: isOverdue ? 'error.text' : 'text.secondary',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap'
          }}
          variant="caption"
        >
          {etaText}
        </Text>
      </Box>
    </Box>
  )
}

RunningRow.displayName = 'RunningRow'

export interface DataRunsRunningNowProps {
  onSelectJobByName: (jobName: string, customerId?: string) => void
  running: RunningJob[]
}

/**
 * "Pågår nu" - the runs that have not finished yet, with how long each has
 * been going and when it is expected to land. First section on the page:
 * everything below it describes runs that are already over.
 *
 * A job only shows up here about a minute after it starts, and never at all
 * if it finishes faster than that - Glue reports an in-flight run through
 * its delay notification, whose threshold cannot be set below one minute.
 *
 * Rows have no `JobSummary` of their own (this list is built from the
 * running runs, not the job summaries), so clicks go through
 * `onSelectJobByName` like the order warnings do.
 */
const DataRunsRunningNow = (props: DataRunsRunningNowProps) => {
  const { onSelectJobByName, running } = props

  const nowMs = useNow(TICK_INTERVAL_MS)
  const lastFinish = latestExpectedFinish(running)

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '20px',
        p: '20px 22px'
      }}
    >
      <SectionHeader
        body={
          lastFinish
            ? `Senast förväntad klar ca ${formatClock(lastFinish)}`
            : undefined
        }
        icon={
          <Box
            sx={{
              bgcolor: 'info.main',
              borderRadius: '50%',
              height: '10px',
              width: '10px',
              ...(running.length > 0 ? RUNNING_PULSE_SX : null)
            }}
          />
        }
        title="Pågår nu"
        titleAdornment={
          <Chip
            color={running.length > 0 ? 'info' : 'default'}
            label={String(running.length)}
            size="small"
          />
        }
      />

      {running.length === 0 ? (
        <Text sx={{ color: 'text.disabled', mt: 1 }} variant="body2">
          Inga jobb kör just nu.
        </Text>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
          {running.map((job) => (
            <RunningRow
              job={job}
              key={jobLookupKey(job.job_name, job.customer?.id)}
              nowMs={nowMs}
              onSelectJobByName={onSelectJobByName}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

DataRunsRunningNow.displayName = 'DataRunsRunningNow'
export default DataRunsRunningNow
